import React, { FC, useContext } from 'react'
import { ThemeContext } from 'styled-components'

import {
    OuterCardWrapper,
    CardContainer,
    ImageSection,
    ImageWrapper,
    NextImageContainer,
    ContentSection,
    Title,
    StatsRow,
    RatingRow,
    RatingText,
    RatingCount,
    Description,
    CTAButton,
    PriceContainer,
    PersonDetails,
    BottomRow,
    PriceWrapper,
    ButtonWrapper,
    FavoriteButton,
    FeaturesWrapper,
    ObjectTopRecommendationPill,
    CheckBox,
    InformationWrapper,
    InputCheckbox,
    CheckBoxLabel,
    BreadcrumbContainer,
    LocationContainer,
    LocationText,
    SeoHref,
    PopularChoiceTagContainer,
    PopularChoiceTagText,
    TraumTagIconStyled,
    TopTraumTagPill,
    TraumTagLabel,
    OfferBadge,
    OfferIcon,
    OfferText,
    FreeCancellationContainer,
    FreeCancellationIcon,
    FreeCancellationText,
    PriceText,
    OfferWrapper,
    BottomTraumTagContainer,
    BottomTraumTagImage,
    BottomTraumTagText,
    OfferPriceWrapper,
    ContentHeader,
    RegionTitleContainer,
    QuickResponderTag,
    StyledFastResponderIcon,
    BookingHighlights,
    PartnerLogoContainer,
    PartnerLogo,
} from './styles'
import StarsContainer from '../../common/stars-container'
import WishlistSection from '../../common/wishlist-section'
import { ListingSectionObject } from '../../../transports/common/listing-section-transport'
import { useTranslation } from '../../../hooks/locale'
import InlineFeatures from '../features/features'
import NextImage from '../next-image'
import { Features } from '../../../models/common/features-model'
import BreadcrumbSection from '../../landing-page/breadcrumb-section'
import { useCardData } from '../../../hooks/use-card-data'
import { stopPropagation } from '../../../utils/dom-utils'
import ImageCarousel from '../../common/image-carousel'
import { getPartnerLogoUrl } from '../../../utils/miscellaneous-utils'
import { BreadcrumbListTransport } from '../../../models/common/breadcrumb-model'
import { Offer } from '../../../models/common/offers-model'
import { SuitablesModel } from '../../../models/common/suitables-model'

interface Props {
    listingObject: ListingSectionObject
    pageName: string
    sectionName: string
    features?: Features
    showCtaButton?: boolean
    showBreadcrumbsSection?: boolean
    showMaxPersonCount?: boolean
    showCheckbox?: boolean
    isSelected?: boolean
    onToggleSelect?: (id: number) => void
    sitePathname?: string
    showDescription?: boolean
    showRegion?: boolean
    useCarousel?: boolean
    breadcrumbModel: BreadcrumbListTransport
    offerModel: Offer
    suitablesModel: SuitablesModel
}

const MAX_STARS = 5
const MAX_VISIBLE_FEATURES = 5

const ObjectOverviewCard: FC<Props> = ({
    listingObject,
    pageName,
    sectionName,
    features,
    showCtaButton = true,
    showBreadcrumbsSection = true,
    showMaxPersonCount = false,
    showCheckbox = false,
    isSelected = false,
    onToggleSelect,
    sitePathname,
    showDescription = true,
    showRegion = true,
    useCarousel = false,
    breadcrumbModel,
    offerModel,
    suitablesModel,
}) => {
    const { t } = useTranslation()
    const theme = useContext(ThemeContext)

    const cardData = useCardData({
        listingObject,
        features,
        showCheckbox,
        isSelected,
        onToggleSelect,
        isObjectOverview: true,
        showMaxPersonCount,
        showCtaButton,
        sitePathname,
        pageName,
        sectionName,
        breadcrumbModel,
        offerModel,
        suitablesModel,
    })

    const {
        listingId,
        propertyTitle,
        propertyDescriptionText,
        regionName,
        regionLink,
        cardImages,
        partnerName,
        priceValue,
        priceInfoValue,
        formattedOffer,
        isFreeCancellation,
        shouldShowRating,
        formattedRatingAverage,
        formattedRatingCount,
        hasFeatures,
        traumTagsData,
        currentBadgeTraumTag,
        isTopObject,
        showSuperfastResponderTag,
        isDirectBooking,
        truncatedDescription,
        suitablesData,
        handleCardClick,
        handleCheckboxChange,
        handleCtaClick,
        href,
        breadcrumbs,
    } = cardData

    return (
        <>
            {showBreadcrumbsSection && breadcrumbs && (
                <BreadcrumbContainer>
                    <BreadcrumbSection
                        breadcrumbs={breadcrumbModel?.format(breadcrumbs, '') || []}
                        url={href}
                        hideMobile={false}
                        containerPadding="0"
                    />
                </BreadcrumbContainer>
            )}
            <OuterCardWrapper onClick={handleCardClick}>
                <CardContainer>
                    <ImageSection>
                        <ImageWrapper>
                            {showCheckbox && (
                                <InformationWrapper onClick={stopPropagation}>
                                    <CheckBox>
                                        <InputCheckbox
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={handleCheckboxChange}
                                            id={`checkbox-${listingId}`}
                                        />
                                        <CheckBoxLabel htmlFor={`checkbox-${listingId}`} />
                                    </CheckBox>
                                </InformationWrapper>
                            )}
                            {useCarousel ? (
                                <ImageCarousel
                                    images={cardImages}
                                    aspectRatio="66.66%"
                                    imageCarouselBorderRadius="1rem"
                                    imageCarouselMargin="0"
                                />
                            ) : (
                                cardImages.length > 0 && (
                                    <NextImage
                                        containerComponent={NextImageContainer}
                                        src={cardImages[0].url}
                                        alt={cardImages[0].alt || propertyTitle}
                                        layout="responsive"
                                        width={3}
                                        height={2}
                                    />
                                )
                            )}
                            {isTopObject && (
                                <ObjectTopRecommendationPill>{t('topListing')}</ObjectTopRecommendationPill>
                            )}
                            {traumTagsData.badgeTraumTags.length > 0 &&
                                currentBadgeTraumTag &&
                                currentBadgeTraumTag.Icon && (
                                    <TopTraumTagPill key={currentBadgeTraumTag.id} $showCheckbox={showCheckbox}>
                                        <TraumTagIconStyled as={currentBadgeTraumTag.Icon} />
                                        <TraumTagLabel>{t(currentBadgeTraumTag.label)}</TraumTagLabel>
                                    </TopTraumTagPill>
                                )}
                            {partnerName ? (
                                <PartnerLogoContainer $partnerName={partnerName}>
                                    <PartnerLogo
                                        src={getPartnerLogoUrl(partnerName)}
                                        alt={`${partnerName} logo`}
                                        $partnerName={partnerName}
                                    />
                                </PartnerLogoContainer>
                            ) : (
                                <FavoriteButton onClick={stopPropagation}>
                                    <WishlistSection
                                        containerBorderRadius="1.456rem"
                                        containerHeight="1.5rem"
                                        containerWidth="1.5rem"
                                        containerMargin="0"
                                        containerPadding="0"
                                        width="1.5rem"
                                        height="1.5rem"
                                        listingId={listingId?.toString() || ''}
                                        showBoxShadow={false}
                                        strokeColor="white"
                                        containerBackgroundColor="transparent"
                                    />
                                </FavoriteButton>
                            )}
                        </ImageWrapper>
                        <OfferWrapper>
                            {traumTagsData.bottomTraumTags.map(tag => (
                                <BottomTraumTagContainer key={tag.id}>
                                    {tag.imageUrl && <BottomTraumTagImage src={tag.imageUrl} alt={t(tag.label)} />}
                                    <BottomTraumTagText>{t(tag.label)}</BottomTraumTagText>
                                </BottomTraumTagContainer>
                            ))}
                        </OfferWrapper>
                    </ImageSection>

                    <ContentSection>
                        <ContentHeader>
                            <RegionTitleContainer>
                                {showRegion && (
                                    <LocationContainer>
                                        <SeoHref title={regionName} href={regionLink} onClick={stopPropagation}>
                                            <LocationText>{regionName}</LocationText>
                                        </SeoHref>
                                    </LocationContainer>
                                )}
                                <Title>{propertyTitle}</Title>
                            </RegionTitleContainer>
                            {traumTagsData.popularChoiceTag && (
                                <PopularChoiceTagContainer>
                                    {traumTagsData.popularChoiceTag.Icon && (
                                        <TraumTagIconStyled as={traumTagsData.popularChoiceTag.Icon} />
                                    )}
                                    <PopularChoiceTagText>
                                        {t(traumTagsData.popularChoiceTag.label)}
                                    </PopularChoiceTagText>
                                </PopularChoiceTagContainer>
                            )}
                        </ContentHeader>

                        {propertyDescriptionText && <StatsRow>{propertyDescriptionText}</StatsRow>}

                        {shouldShowRating && (
                            <RatingRow>
                                <RatingText>{formattedRatingAverage}</RatingText>
                                <StarsContainer averageRating={Number(formattedRatingAverage)} maxStars={MAX_STARS} />
                                <RatingCount>{formattedRatingCount}</RatingCount>
                            </RatingRow>
                        )}

                        <FeaturesWrapper>
                            {(suitablesData || hasFeatures) && (
                                <InlineFeatures
                                    color={theme.colors.tfwBlack99}
                                    size={1.25}
                                    gap={0.75}
                                    visibleFeatures={MAX_VISIBLE_FEATURES}
                                    features={features || null}
                                />
                            )}
                        </FeaturesWrapper>

                        {showDescription && <Description>{truncatedDescription}</Description>}

                        <BookingHighlights>
                            {showSuperfastResponderTag && (
                                <QuickResponderTag>
                                    <StyledFastResponderIcon /> {t('quickResponderText')}
                                </QuickResponderTag>
                            )}
                            {isDirectBooking && (
                                <QuickResponderTag>
                                    <StyledFastResponderIcon /> {t('directBookingTagLabel')}
                                </QuickResponderTag>
                            )}
                        </BookingHighlights>

                        <BottomRow>
                            <OfferPriceWrapper>
                                {formattedOffer && (
                                    <OfferBadge>
                                        <OfferIcon />
                                        <OfferText>
                                            {formattedOffer?.discount || ''} ● {formattedOffer?.label || ''}
                                        </OfferText>
                                    </OfferBadge>
                                )}
                                <PriceWrapper>
                                    <PriceContainer>
                                        {priceValue && <PriceText>{priceValue}</PriceText>}
                                        {isFreeCancellation && (
                                            <FreeCancellationContainer>
                                                <FreeCancellationIcon />
                                                <FreeCancellationText>{t('freeCancellation')}</FreeCancellationText>
                                            </FreeCancellationContainer>
                                        )}
                                    </PriceContainer>
                                    {priceInfoValue && <PersonDetails>{priceInfoValue}</PersonDetails>}
                                </PriceWrapper>
                            </OfferPriceWrapper>

                            {showCtaButton && (
                                <ButtonWrapper>
                                    <CTAButton onClick={handleCtaClick}>{t('enquire')}</CTAButton>
                                </ButtonWrapper>
                            )}
                        </BottomRow>
                    </ContentSection>
                </CardContainer>
            </OuterCardWrapper>
        </>
    )
}

export default ObjectOverviewCard
